import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { StackerBundle, StackerBundleDraft } from "../models/StackerBundle";
import { DisplayRow, EditRow } from "./StackerRow";

type Props = {
	rows: StackerBundle[];
	shiftId: number;
	draftRow: StackerBundleDraft | null;
	editingId: number | null;
	isMutating: boolean;
	onStartEdit: (bundle: StackerBundle) => void;
	onCancelEdit: () => void;
	onSaveDraft: (draft: StackerBundleDraft) => void;
	onDelete: (bundle: StackerBundle) => void;
	onDraftChange: (draft: StackerBundleDraft) => void;
};

export const StackerBundlesGrid = ({
	rows,
	draftRow,
	editingId,
	isMutating,
	onStartEdit,
	onCancelEdit,
	onDraftChange,
	onSaveDraft,
	onDelete,
}: Props) => {
	const goodBundles = rows.reduce((sum, r) => sum + (r.GoodBundles || 0), 0);
	const totalBundles = rows.reduce(
		(sum, r) => sum + (r.GoodBundles || 0) + (r.ScrapBundles || 0),
		0,
	);
	const goodPieces = rows.reduce((sum, r) => sum + (r.TotalPieces || 0), 0);
	const totalPieces = rows.reduce(
		(sum, r) =>
			sum +
			(r.TotalPieces || 0) +
			(r.ScrapBundles || 0) * (r.ScrapPiecesPerBundle || 0),
		0,
	);
	const totalWeight = rows.reduce((sum, r) => sum + (r.TotalWeight || 0), 0);

	const groups = [
		{ label: "Bundles", good: goodBundles, total: totalBundles },
		{ label: "Pieces", good: goodPieces, total: totalPieces },
	];

	return (
		<Paper elevation={2} sx={{ overflow: "hidden" }}>
			<Box
				sx={{
					px: 2.5,
					py: 1.5,
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
				}}
			>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					Finished Bundles
				</Typography>
			</Box>

			<TableContainer>
				<Table
					size="small"
					sx={{
						tableLayout: "fixed",
						width: "100%",
						"& .MuiTableHead-root .MuiTableCell-root": {
							fontSize: (theme) => theme.typography.caption.fontSize,
							fontWeight: 600,
							color: (theme) => theme.palette.text.secondary,
						},
						"& .MuiTableBody-root .MuiTableCell-root": {
							fontSize: (theme) => theme.typography.body2.fontSize,
							fontWeight: 400,
						},
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: "2%" }}>#</TableCell>
							<TableCell sx={{ width: "10%" }}>Section</TableCell>
							<TableCell sx={{ width: "10%" }}>Heat</TableCell>
							<TableCell sx={{ width: "10%" }}>Grade</TableCell>
							<TableCell sx={{ width: "9%" }}>Length</TableCell>
							<TableCell sx={{ width: "11%" }}>Good (Bdl × Pcs)</TableCell>
							<TableCell sx={{ width: "8%" }}>Total Pieces</TableCell>
							<TableCell sx={{ width: "8%" }}>Total Weight</TableCell>
							<TableCell sx={{ width: "11%" }}>Scrap (Bdl × Pcs)</TableCell>
							<TableCell sx={{ width: "16%" }}>Reason for Scrap</TableCell>
							<TableCell align="right" sx={{ width: "5%" }}>
								Actions
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row, idx) => {
							const isEditing =
								editingId === row.StackerBundleId && draftRow !== null;
							if (isEditing && draftRow) {
								return (
									<EditRow
										key={`edit-${row.StackerBundleId}`}
										idx={idx + 1}
										draft={draftRow}
										original={row}
										isMutating={isMutating}
										onChange={onDraftChange}
										onSave={() => onSaveDraft(draftRow)}
										onCancel={onCancelEdit}
									/>
								);
							}
							return (
								<DisplayRow
									key={row.StackerBundleId}
									idx={idx + 1}
									row={row}
									disabled={editingId !== null || isMutating}
									onEdit={() => onStartEdit(row)}
									onDelete={() => onDelete(row)}
								/>
							);
						})}

						{draftRow && draftRow.StackerBundleId === null && (
							<EditRow
								key="new-draft"
								idx={rows.length + 1}
								draft={draftRow}
								isMutating={isMutating}
								onChange={onDraftChange}
								onSave={() => onSaveDraft(draftRow)}
								onCancel={onCancelEdit}
							/>
						)}

						{rows.length === 0 && !draftRow && (
							<TableRow>
								<TableCell colSpan={12} sx={{ py: 4, textAlign: "center" }}>
									<Typography variant="body2" color="text.secondary">
										No bundles for this shift yet. Click <b>+ Add Bundle Row</b>{" "}
										to create the first one.
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Box
				sx={{
					px: 2.5,
					py: 1.25,
					borderTop: "1px solid",
					borderColor: "divider",
					backgroundColor: "grey.50",
					display: "flex",
					flexWrap: "wrap",
					justifyContent: "flex-end",
					columnGap: 3,
					rowGap: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ fontWeight: 500, mr: 0.25 }}
					>
						Total Weight:
					</Typography>
					<Typography
						variant="h6"
						color="primary.main"
						sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
					>
						{totalWeight.toLocaleString()}
					</Typography>
				</Box>
				{groups.map(({ label, good, total }) => (
					<Box
						key={label}
						sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
					>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 500, mr: 0.25 }}
						>
							{label}:
						</Typography>
						<Typography
							variant="h6"
							color="success.main"
							sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
						>
							{good.toLocaleString()}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							good
						</Typography>
						<Typography
							variant="h6"
							color="text.disabled"
							sx={{ fontWeight: 300, mx: 0.25 }}
						>
							/
						</Typography>
						<Typography
							variant="h6"
							color="primary.main"
							sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
						>
							{total.toLocaleString()}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							total
						</Typography>
					</Box>
				))}
			</Box>
		</Paper>
	);
};
